import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
  useColorMode,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftAddon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  StarIcon,
} from "@chakra-ui/icons";
import {
  FaCoins,
  FaChartPie,
  FaCalculator,
  FaDollarSign,
  FaChartLine,
  FaUsers,
  FaCrown,
  FaRocket,
  FaGift,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const Tokenomics = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const [tokenAmount, setTokenAmount] = useState(10000);
  const [selectedScenario, setSelectedScenario] = useState(10000);

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );

  // Payout calculator logic
  const calculatePayout = (tokens: number, monthlyProfit: number) => {
    const totalTokens = 1000000;
    const distributablePercent = 0.7;
    const distributableProfit = monthlyProfit * distributablePercent;
    const perTokenPayout = distributableProfit / totalTokens;
    return (tokens * perTokenPayout).toFixed(2);
  };

  const scenarios = [
    { profit: 5000, label: "$5K" },
    { profit: 10000, label: "$10K" },
    { profit: 20000, label: "$20K" },
    { profit: 50000, label: "$50K" },
  ];

  const investmentTiers = [
    {
      name: "Early Adopter",
      investment: 500,
      tokens: 1000,
      bonus: 0,
      totalTokens: 1000,
      color: "#3B82F6",
      benefits: ["BB Token ownership", "Monthly profit distributions", "Governance voting rights"],
    },
    {
      name: "Supporter",
      investment: 2000,
      tokens: 4000,
      bonus: 5,
      totalTokens: 4200,
      color: "#10B981",
      benefits: ["5% bonus tokens", "Priority support", "Early feature access", "All Early Adopter benefits"],
    },
    {
      name: "Builder",
      investment: 5000,
      tokens: 10000,
      bonus: 10,
      totalTokens: 11000,
      color: "#F59E0B",
      benefits: ["10% bonus tokens", "Advisory council consideration", "Quarterly strategy calls", "All Supporter benefits"],
    },
    {
      name: "Pioneer",
      investment: 10000,
      tokens: 25000,
      bonus: 25,
      totalTokens: 31250,
      color: "#9333EA",
      benefits: ["25% BONUS tokens", "Guaranteed advisory council seat", "Monthly 1-on-1 with Tom", "VIP status", "All Builder benefits"],
      featured: true,
    },
  ];

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="1400px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero */}
          <Box textAlign="center">
            <Badge colorScheme="purple" fontSize="md" px={4} py={2} mb={4}>
              Token Economics
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl" }}
              fontWeight="800"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #9333EA, #F59E0B)"
              bgClip="text"
            >
              Business Builder Token (BB)
            </Heading>
            <Text fontSize="xl" color={textSecondary} maxW="800px" mx="auto">
              Participate in the platform's growth through tokenized ownership.
              Monthly profit distributions, governance rights, and aligned incentives.
            </Text>
          </Box>

          {/* Token Distribution */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaChartPie} w={7} h={7} color="#9333EA" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Token Distribution
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Heading size="md" mb={4} color={textPrimary}>
                    Total Supply: 1,000,000 BB
                  </Heading>
                  <Text fontSize="md" mb={6} color={textSecondary}>
                    Fixed supply, non-inflationary. No additional minting possible.
                  </Text>
                  <VStack spacing={4} align="stretch">
                    {[
                      {
                        label: "Founder Allocation (Tom)",
                        tokens: "400,000 BB",
                        percent: "40%",
                        color: "#9333EA",
                        desc: "Recognition of IP investment and infrastructure development",
                      },
                      {
                        label: "Investment Pool",
                        tokens: "500,000 BB",
                        percent: "50%",
                        color: "#10B981",
                        desc: "Available for purchase by investors",
                      },
                      {
                        label: "Development Fund",
                        tokens: "100,000 BB",
                        percent: "10%",
                        color: "#F59E0B",
                        desc: "Bug bounties, feature rewards, contributor incentives",
                      },
                    ].map((item, index) => (
                      <Box
                        key={index}
                        p={4}
                        bg={`${item.color}20`}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={`${item.color}40`}
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="700" color={textPrimary}>
                            {item.label}
                          </Text>
                          <Badge
                            bg={item.color}
                            color="white"
                            fontSize="md"
                            px={3}
                            py={1}
                          >
                            {item.percent}
                          </Badge>
                        </Flex>
                        <Text fontSize="lg" fontWeight="600" color={item.color} mb={1}>
                          {item.tokens}
                        </Text>
                        <Text fontSize="sm" color={textSecondary}>
                          {item.desc}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
                <Box>
                  <Box
                    bg="rgba(147, 51, 234, 0.1)"
                    p={6}
                    borderRadius="xl"
                    borderWidth="2px"
                    borderColor="rgba(147, 51, 234, 0.3)"
                    textAlign="center"
                    mb={6}
                  >
                    <Icon as={FaChartPie} w={20} h={20} color="#9333EA" mb={4} />
                    <Heading size="md" mb={4} color={textPrimary}>
                      Token Distribution Pie Chart
                    </Heading>
                    <SimpleGrid columns={3} spacing={4}>
                      <Box>
                        <Box
                          w="60px"
                          h="60px"
                          bg="#9333EA"
                          borderRadius="full"
                          mx="auto"
                          mb={2}
                        />
                        <Text fontSize="xs" color={textSecondary}>
                          Founder 40%
                        </Text>
                      </Box>
                      <Box>
                        <Box
                          w="60px"
                          h="60px"
                          bg="#10B981"
                          borderRadius="full"
                          mx="auto"
                          mb={2}
                        />
                        <Text fontSize="xs" color={textSecondary}>
                          Investment 50%
                        </Text>
                      </Box>
                      <Box>
                        <Box
                          w="60px"
                          h="60px"
                          bg="#F59E0B"
                          borderRadius="full"
                          mx="auto"
                          mb={2}
                        />
                        <Text fontSize="xs" color={textSecondary}>
                          Dev Fund 10%
                        </Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Box
                    bg="rgba(59, 130, 246, 0.1)"
                    p={6}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor="rgba(59, 130, 246, 0.3)"
                  >
                    <Heading size="sm" mb={3} color={textPrimary}>
                      Token Utility
                    </Heading>
                    <List spacing={2}>
                      <ListItem fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        Monthly profit distributions (70% of profit)
                      </ListItem>
                      <ListItem fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        Governance voting rights (1 token = 1 vote)
                      </ListItem>
                      <ListItem fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        Early access to new features
                      </ListItem>
                      <ListItem fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        Priority support and consultation
                      </ListItem>
                      <ListItem fontSize="sm" color={textSecondary}>
                        <ListIcon as={CheckIcon} color="#10B981" />
                        Voting power delegation
                      </ListItem>
                    </List>
                  </Box>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Investment Tiers */}
          <Box>
            <Heading size="xl" mb={4} textAlign="center" color={textPrimary}>
              Investment Tiers
            </Heading>
            <Text fontSize="lg" mb={10} textAlign="center" color={textSecondary} maxW="800px" mx="auto">
              Choose your investment level. Higher tiers receive bonus tokens and additional benefits.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              {investmentTiers.map((tier, index) => (
                <Card
                  key={index}
                  bg={tier.featured ? `${tier.color}20` : cardGradientBg}
                  borderWidth={tier.featured ? "3px" : "1px"}
                  borderColor={tier.featured ? tier.color : cardBorder}
                  boxShadow={
                    tier.featured
                      ? `0 12px 40px ${tier.color}40`
                      : "0 4px 16px rgba(0, 0, 0, 0.2)"
                  }
                  position="relative"
                  transition="all 0.3s"
                  _hover={{
                    transform: "translateY(-4px)",
                    boxShadow: `0 16px 48px ${tier.color}60`,
                  }}
                >
                  {tier.featured && (
                    <Badge
                      position="absolute"
                      top="-12px"
                      left="50%"
                      transform="translateX(-50%)"
                      bg="linear-gradient(135deg, #9333EA, #7C3AED)"
                      color="white"
                      px={4}
                      py={1}
                      fontSize="sm"
                      fontWeight="700"
                    >
                      <Icon as={FaCrown} mr={1} />
                      BEST VALUE
                    </Badge>
                  )}
                  <CardBody p={6} textAlign="center">
                    <Icon
                      as={tier.featured ? FaCrown : FaCoins}
                      w={12}
                      h={12}
                      color={tier.color}
                      mb={4}
                    />
                    <Heading size="lg" mb={2} color={textPrimary}>
                      {tier.name}
                    </Heading>
                    <Text fontSize="4xl" fontWeight="800" color={tier.color} mb={2}>
                      ${tier.investment.toLocaleString()}
                    </Text>
                    <Text fontSize="sm" color={textSecondary} mb={4}>
                      Investment Amount
                    </Text>
                    <Divider my={4} />
                    <Stat mb={4}>
                      <StatLabel fontSize="sm" color={textSecondary}>
                        Base Tokens
                      </StatLabel>
                      <StatNumber fontSize="2xl" color={textPrimary}>
                        {tier.tokens.toLocaleString()} BB
                      </StatNumber>
                      {tier.bonus > 0 && (
                        <StatHelpText color="#10B981" fontSize="md" fontWeight="600">
                          <Icon as={FaGift} mr={1} />+ {tier.bonus}% BONUS ({(tier.totalTokens - tier.tokens).toLocaleString()} BB)
                        </StatHelpText>
                      )}
                    </Stat>
                    <Text fontSize="xl" fontWeight="700" color={tier.color} mb={4}>
                      = {tier.totalTokens.toLocaleString()} BB Total
                    </Text>
                    <Divider my={4} />
                    <List spacing={2} textAlign="left" mb={6}>
                      {tier.benefits.map((benefit, i) => (
                        <ListItem key={i} fontSize="sm" color={textSecondary}>
                          <ListIcon as={CheckIcon} color={tier.color} />
                          {benefit}
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      width="100%"
                      bg={tier.color}
                      color="white"
                      size="lg"
                      _hover={{
                        bg: tier.color,
                        opacity: 0.9,
                      }}
                      rightIcon={<ChevronRightIcon />}
                    >
                      Invest Now
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* 20 Investor / $1M Funding Scenario */}
          <Card
            bg="linear-gradient(135deg, rgba(147, 51, 234, 0.15), rgba(79, 70, 229, 0.15))"
            backdropFilter="blur(10px)"
            boxShadow="0 12px 48px rgba(147, 51, 234, 0.4)"
            borderRadius="2xl"
            borderWidth="3px"
            borderColor="rgba(147, 51, 234, 0.6)"
            position="relative"
          >
            <Box
              position="absolute"
              top="0"
              right="0"
              left="0"
              height="8px"
              bg="linear-gradient(90deg, #9333EA 0%, #F59E0B 50%, #10B981 100%)"
              borderTopRadius="2xl"
            />
            <CardHeader
              bg="rgba(147, 51, 234, 0.2)"
              borderBottomWidth="2px"
              borderColor="rgba(147, 51, 234, 0.4)"
              pt={8}
            >
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={4}>
                <Box>
                  <Badge
                    bg="#F59E0B"
                    color="white"
                    fontSize="sm"
                    px={3}
                    py={1}
                    mb={3}
                  >
                    TARGET SCENARIO
                  </Badge>
                  <Heading size="xl" color={textPrimary} mb={2}>
                    20 Investors • $1,000,000 Raised
                  </Heading>
                  <Text fontSize="md" color={textSecondary}>
                    How Tom plans to raise capital through strategic investor partnerships
                  </Text>
                </Box>
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="xl"
                  textAlign="center"
                  minW="200px"
                >
                  <Text fontSize="sm" color={textSecondary} mb={1}>
                    Total Raise
                  </Text>
                  <Text fontSize="4xl" fontWeight="800" color="#10B981">
                    $1M
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    From 20 investors
                  </Text>
                </Box>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="lg" mb={6} color={textSecondary} textAlign="center" maxW="900px" mx="auto">
                This scenario shows a realistic path to $1M funding with 20 strategic
                investors, each contributing based on their capacity and belief in the
                Business Builder vision.
              </Text>

              <Heading size="md" mb={6} color={textPrimary} textAlign="center">
                Investor Breakdown
              </Heading>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={8}>
                {[
                  {
                    count: 8,
                    tier: "Pioneer",
                    investment: 10000,
                    total: 80000,
                    tokens: 31250,
                    color: "#9333EA",
                  },
                  {
                    count: 6,
                    tier: "Builder",
                    investment: 5000,
                    total: 30000,
                    tokens: 11000,
                    color: "#F59E0B",
                  },
                  {
                    count: 4,
                    tier: "Supporter",
                    investment: 2000,
                    total: 8000,
                    tokens: 4200,
                    color: "#10B981",
                  },
                  {
                    count: 2,
                    tier: "Custom",
                    investment: 41000,
                    total: 82000,
                    tokens: 102500,
                    color: "#3B82F6",
                    note: "Large investors",
                  },
                ].map((group, index) => (
                  <Box
                    key={index}
                    p={5}
                    bg={`${group.color}20`}
                    borderRadius="lg"
                    borderWidth="2px"
                    borderColor={`${group.color}40`}
                    textAlign="center"
                  >
                    <Text fontSize="3xl" fontWeight="800" color={group.color} mb={1}>
                      {group.count}
                    </Text>
                    <Text fontSize="sm" color={textSecondary} mb={3}>
                      {group.tier} Investors
                    </Text>
                    <Divider mb={3} />
                    <Text fontSize="xs" color={textMuted} mb={1}>
                      @ ${group.investment.toLocaleString()} each
                    </Text>
                    <Text fontSize="lg" fontWeight="700" color={textPrimary} mb={1}>
                      ${group.total.toLocaleString()}
                    </Text>
                    <Text fontSize="xs" color={textSecondary}>
                      {(group.tokens * group.count).toLocaleString()} BB tokens
                    </Text>
                    {group.note && (
                      <Badge colorScheme="blue" mt={2} fontSize="xs">
                        {group.note}
                      </Badge>
                    )}
                  </Box>
                ))}
              </SimpleGrid>

              <Box
                bg="rgba(16, 185, 129, 0.1)"
                p={6}
                borderRadius="xl"
                borderWidth="2px"
                borderColor="rgba(16, 185, 129, 0.4)"
                mb={6}
              >
                <Heading size="md" mb={4} color={textPrimary} textAlign="center">
                  Total Token Distribution
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <Stat textAlign="center">
                    <StatLabel color={textSecondary}>Total Raised</StatLabel>
                    <StatNumber fontSize="3xl" color="#10B981">
                      $1,000,000
                    </StatNumber>
                    <StatHelpText>From 20 investors</StatHelpText>
                  </Stat>
                  <Stat textAlign="center">
                    <StatLabel color={textSecondary}>Tokens Sold</StatLabel>
                    <StatNumber fontSize="3xl" color="#9333EA">
                      500,000 BB
                    </StatNumber>
                    <StatHelpText>100% of investment pool</StatHelpText>
                  </Stat>
                  <Stat textAlign="center">
                    <StatLabel color={textSecondary}>Avg Investment</StatLabel>
                    <StatNumber fontSize="3xl" color="#F59E0B">
                      $50,000
                    </StatNumber>
                    <StatHelpText>Per investor</StatHelpText>
                  </Stat>
                </SimpleGrid>
              </Box>

              <Heading size="md" mb={4} color={textPrimary} textAlign="center">
                What This Means at Different Profit Levels
              </Heading>
              <Text fontSize="sm" mb={6} color={textSecondary} textAlign="center">
                Monthly payout examples based on platform profit (after expenses)
              </Text>

              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Investor Tier</Th>
                    <Th color={textSecondary} isNumeric>
                      BB Tokens
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      @ $5K Profit
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      @ $10K Profit
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      @ $20K Profit
                    </Th>
                    <Th color={textSecondary} isNumeric>
                      @ $50K Profit
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    { name: "Pioneer ($10K)", tokens: 31250 },
                    { name: "Builder ($5K)", tokens: 11000 },
                    { name: "Supporter ($2K)", tokens: 4200 },
                    { name: "Large Investor ($41K)", tokens: 51250 },
                  ].map((investor, index) => (
                    <Tr key={index}>
                      <Td fontWeight="600" color={textPrimary}>
                        {investor.name}
                      </Td>
                      <Td isNumeric color={textSecondary}>
                        {investor.tokens.toLocaleString()}
                      </Td>
                      <Td isNumeric color="#10B981" fontWeight="600">
                        ${calculatePayout(investor.tokens, 5000)}
                      </Td>
                      <Td isNumeric color="#10B981" fontWeight="600">
                        ${calculatePayout(investor.tokens, 10000)}
                      </Td>
                      <Td isNumeric color="#10B981" fontWeight="700" fontSize="lg">
                        ${calculatePayout(investor.tokens, 20000)}
                      </Td>
                      <Td isNumeric color="#10B981" fontWeight="700" fontSize="lg">
                        ${calculatePayout(investor.tokens, 50000)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              <Box
                mt={8}
                p={6}
                bg="rgba(245, 158, 11, 0.1)"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="rgba(245, 158, 11, 0.3)"
              >
                <Flex align="center" mb={4}>
                  <Icon as={FaUsers} w={8} h={8} color="#F59E0B" mr={3} />
                  <Heading size="sm" color={textPrimary}>
                    Tom's 40% Founder Allocation
                  </Heading>
                </Flex>
                <Text fontSize="md" color={textSecondary} mb={3}>
                  With 400,000 BB tokens (40% of total supply), Tom's monthly distributions at different profit levels:
                </Text>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {[5000, 10000, 20000, 50000].map((profit) => (
                    <Box key={profit} textAlign="center">
                      <Text fontSize="xs" color={textMuted}>
                        ${profit.toLocaleString()}/mo profit
                      </Text>
                      <Text fontSize="2xl" fontWeight="700" color="#F59E0B">
                        ${calculatePayout(400000, profit)}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>

              <Box
                mt={6}
                p={5}
                bg="rgba(59, 130, 246, 0.1)"
                borderRadius="md"
                borderWidth="1px"
                borderColor="rgba(59, 130, 246, 0.3)"
              >
                <Text fontSize="sm" fontStyle="italic" color={textSecondary} textAlign="center">
                  <strong>Note:</strong> This is a target scenario showing how 20 strategic
                  investors could collectively raise $1M. Actual investor mix may vary.
                  All calculations based on 70% profit distribution rate.
                </Text>
              </Box>
            </CardBody>
          </Card>

          {/* Payout Calculator */}
          <Card
            bg="linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(16, 185, 129, 0.1))"
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px rgba(34, 197, 94, 0.3)"
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(34, 197, 94, 0.4)"
          >
            <CardHeader
              bg="rgba(34, 197, 94, 0.2)"
              borderBottomWidth="1px"
              borderColor="rgba(34, 197, 94, 0.3)"
            >
              <Flex align="center">
                <Icon as={FaCalculator} w={7} h={7} color="#10B981" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  Profit Distribution Calculator
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="lg" mb={6} color={textSecondary} textAlign="center">
                Calculate your potential monthly payouts based on token holdings and platform profit.
              </Text>

              <Box mb={8}>
                <Heading size="md" mb={4} color={textPrimary}>
                  Your BB Token Holdings
                </Heading>
                <Flex gap={4} align="center" mb={4}>
                  <InputGroup size="lg" maxW="300px">
                    <InputLeftAddon>BB</InputLeftAddon>
                    <Input
                      type="number"
                      value={tokenAmount}
                      onChange={(e) => setTokenAmount(Number(e.target.value))}
                      placeholder="10000"
                    />
                  </InputGroup>
                  <Text color={textSecondary}>tokens</Text>
                </Flex>
                <Slider
                  value={tokenAmount}
                  min={1000}
                  max={100000}
                  step={1000}
                  onChange={(val) => setTokenAmount(val)}
                  mb={2}
                >
                  <SliderTrack bg="gray.200">
                    <SliderFilledTrack bg="#10B981" />
                  </SliderTrack>
                  <SliderThumb boxSize={6} bg="#10B981" />
                </Slider>
                <Flex justify="space-between" fontSize="xs" color={textMuted}>
                  <Text>1,000 BB</Text>
                  <Text>100,000 BB</Text>
                </Flex>
              </Box>

              <Heading size="md" mb={4} color={textPrimary}>
                Monthly Payout Scenarios
              </Heading>
              <Text fontSize="sm" mb={4} color={textSecondary}>
                Platform profit × 70% distribution rate ÷ 1,000,000 total tokens × your tokens = your payout
              </Text>

              <Table variant="simple" size="lg">
                <Thead>
                  <Tr>
                    <Th color={textSecondary}>Platform Profit</Th>
                    <Th color={textSecondary} isNumeric>Your Monthly Payout</Th>
                    <Th color={textSecondary} isNumeric>Annual (×12)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {scenarios.map((scenario, index) => (
                    <Tr
                      key={index}
                      bg={
                        selectedScenario === scenario.profit
                          ? "rgba(34, 197, 94, 0.1)"
                          : "transparent"
                      }
                      _hover={{ bg: "rgba(34, 197, 94, 0.05)" }}
                      cursor="pointer"
                      onClick={() => setSelectedScenario(scenario.profit)}
                    >
                      <Td fontWeight="600" color={textPrimary}>
                        {scenario.label} profit
                      </Td>
                      <Td isNumeric fontSize="xl" fontWeight="700" color="#10B981">
                        ${calculatePayout(tokenAmount, scenario.profit)}
                      </Td>
                      <Td isNumeric fontSize="lg" color={textSecondary}>
                        ${(Number(calculatePayout(tokenAmount, scenario.profit)) * 12).toFixed(2)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              <Box
                mt={6}
                p={6}
                bg="rgba(245, 158, 11, 0.1)"
                borderRadius="lg"
                borderWidth="1px"
                borderColor="rgba(245, 158, 11, 0.3)"
              >
                <Heading size="sm" mb={3} color={textPrimary}>
                  Example: Pioneer Tier Investment
                </Heading>
                <Text fontSize="md" color={textSecondary} mb={2}>
                  $10,000 investment = 31,250 BB tokens (with 25% bonus)
                </Text>
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                  {scenarios.map((scenario) => (
                    <Box key={scenario.profit} textAlign="center">
                      <Text fontSize="xs" color={textMuted}>
                        At {scenario.label}/month
                      </Text>
                      <Text fontSize="2xl" fontWeight="700" color="#10B981">
                        ${calculatePayout(31250, scenario.profit)}
                      </Text>
                      <Text fontSize="xs" color={textSecondary}>
                        per month
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </CardBody>
          </Card>

          {/* Value Accrual */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
              <Flex align="center">
                <Icon as={FaChartLine} w={7} h={7} color="#3B82F6" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  How Token Value Grows
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="lg" mb={6} color={textSecondary}>
                BB token value increases as the platform grows. Here's how:
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {[
                  {
                    icon: FaUsers,
                    title: "More Tenants",
                    desc: "Each new tenant site increases monthly recurring revenue",
                    example: "10 tenants at $500/mo = $5K MRR → 50 tenants = $25K MRR",
                    color: "#3B82F6",
                  },
                  {
                    icon: FaRocket,
                    title: "Tenant Upgrades",
                    desc: "Existing tenants upgrade to higher subscription tiers",
                    example: "Upgrade from $500 to $1,500/mo = 3x revenue per tenant",
                    color: "#10B981",
                  },
                  {
                    icon: FaDollarSign,
                    title: "Feature Development",
                    desc: "New modules and features justify higher pricing",
                    example: "AI features launch → $500/mo price increase",
                    color: "#F59E0B",
                  },
                  {
                    icon: FaChartLine,
                    title: "Platform Network Effects",
                    desc: "More users = more value = more tenants = more profit",
                    example: "Exponential growth as platform becomes industry standard",
                    color: "#9333EA",
                  },
                ].map((item, index) => (
                  <Box
                    key={index}
                    p={6}
                    bg={`${item.color}20`}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={`${item.color}40`}
                  >
                    <Icon as={item.icon} w={10} h={10} color={item.color} mb={3} />
                    <Heading size="md" mb={3} color={textPrimary}>
                      {item.title}
                    </Heading>
                    <Text fontSize="md" mb={3} color={textSecondary}>
                      {item.desc}
                    </Text>
                    <Box
                      p={3}
                      bg="rgba(0, 0, 0, 0.1)"
                      borderRadius="md"
                      fontSize="sm"
                      fontStyle="italic"
                      color={textMuted}
                    >
                      {item.example}
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* CTA */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6366F1 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Heading size="2xl" mb={4}>
              Ready to Invest?
            </Heading>
            <Text fontSize="xl" mb={8} maxW="700px" mx="auto">
              Limited supply of 500,000 BB tokens available. Early investors receive bonus tokens.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#9333EA"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
              >
                Invest Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/governance")}
              >
                Learn About Governance
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Tokenomics;
